import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Upload, Space, Typography, Tabs, Table, Tag, Row, Col, Statistic, List, Timeline, Tooltip, Alert, Switch } from 'antd';
import { InboxOutlined, DiffOutlined, PlusOutlined, MinusOutlined, EditOutlined, InfoCircleOutlined, ClockCircleOutlined, EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import { GTFSProcessor } from '../../model/gtfs';
import dayjs from 'dayjs';
import { Line, Column } from '@ant-design/plots';
import DeckGL from '@deck.gl/react';
import { StaticMap } from 'react-map-gl';
import { GeoJsonLayer } from '@deck.gl/layers';
import { PathStyleExtension } from '@deck.gl/extensions';
import config from '../../config';
import { getViewport } from '../../libs/mapLib';
import 'mapbox-gl/dist/mapbox-gl.css';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const MAPBOX_ACCESS_TOKEN = config.maps.mabBox;
const MAPBOX_STYLE = 'mapbox://styles/mapbox/light-v9';

const INITIAL_VIEW_STATE = {
    longitude: 147.638367,
    latitude: -32.32370,
    zoom: 5,
    pitch: 0,
    bearing: 0
};

const RouteComparisonMap = ({ oldShapes, newShapes }) => {
    const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
    const [showOldRoute, setShowOldRoute] = useState(true);
    const [showNewRoute, setShowNewRoute] = useState(true);
    const [isSideBySide, setIsSideBySide] = useState(false);
    const containerRef = useRef(null);

    // Create GeoJSON for old and new shapes
    const createShapeGeoJson = (shapes, color) => ({
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: shapes?.map(s => [parseFloat(s.shape_pt_lon), parseFloat(s.shape_pt_lat)])
                .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1])) || []
        },
        properties: { color }
    });

    // Auto-fit the view to show all points
    useEffect(() => {
        if ((!oldShapes?.length && !newShapes?.length) || !containerRef.current) return;

        const container = containerRef.current;
        const { width, height } = container.getBoundingClientRect();

        // Get all points
        const allPoints = [
            ...(oldShapes || []).map(s => ({ lat: parseFloat(s.shape_pt_lat), lon: parseFloat(s.shape_pt_lon) })),
            ...(newShapes || []).map(s => ({ lat: parseFloat(s.shape_pt_lat), lon: parseFloat(s.shape_pt_lon) }))
        ].filter(p => !isNaN(p.lat) && !isNaN(p.lon));

        if (allPoints.length === 0) return;

        const viewport = getViewport({
            geos: allPoints,
            viewport: { width: isSideBySide ? width / 2 : width, height },
            padding: 50,
            minZoom: 1,
            maxZoom: 20
        });

        setViewState({
            ...viewport,
            transitionDuration: 1000
        });
    }, [oldShapes, newShapes, isSideBySide]);

    // Create layers
    const oldLayers = [
        showOldRoute && new GeoJsonLayer({
            id: 'old-route-path',
            data: createShapeGeoJson(oldShapes, 'red'),
            pickable: false,
            stroked: false,
            filled: false,
            lineWidthMinPixels: 2,
            parameters: {
                depthTest: false
            },
            getLineColor: [239, 68, 68], // Red
            getLineWidth: 3,
            extensions: [new PathStyleExtension({ highPrecisionDash: true })],
            getDashArray: [6, 6]
        })
    ].filter(Boolean);

    const newLayers = [
        showNewRoute && new GeoJsonLayer({
            id: 'new-route-path',
            data: createShapeGeoJson(newShapes, 'green'),
            pickable: false,
            stroked: false,
            filled: false,
            lineWidthMinPixels: 2,
            parameters: {
                depthTest: false
            },
            getLineColor: [52, 211, 153], // Green
            getLineWidth: 3
        })
    ].filter(Boolean);

    // Calculate metrics
    const oldDistance = oldShapes ? calculateShapeDistance(oldShapes) : 0;
    const newDistance = newShapes ? calculateShapeDistance(newShapes) : 0;
    const distanceDiff = newDistance - oldDistance;
    const pointsDiff = (newShapes?.length || 0) - (oldShapes?.length || 0);

    return (
        <Card size="small" title="Route Comparison">
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Switch
                        checked={showOldRoute}
                        onChange={setShowOldRoute}
                        checkedChildren="Old Route"
                        unCheckedChildren="Old Route"
                        style={{ backgroundColor: showOldRoute ? '#ef4444' : '#d1d5db' }}
                    />
                    <Switch
                        checked={showNewRoute}
                        onChange={setShowNewRoute}
                        checkedChildren="New Route"
                        unCheckedChildren="New Route"
                        style={{ backgroundColor: showNewRoute ? '#10b981' : '#d1d5db' }}
                    />
                    <Switch
                        checked={isSideBySide}
                        onChange={setIsSideBySide}
                        checkedChildren="Side by Side"
                        unCheckedChildren="Overlay"
                    />
                </Space>
            </div>
            {isSideBySide ? (
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={{ height: 400, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                            <DeckGL
                                initialViewState={viewState}
                                controller={true}
                                layers={oldLayers}
                                onViewStateChange={({ viewState }) => setViewState(viewState)}
                            >
                                <StaticMap
                                    mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
                                    mapStyle={MAPBOX_STYLE}
                                />
                            </DeckGL>
                        </div>
                        <Text type="danger" strong style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                            Old Route ({oldDistance.toFixed(1)} km)
                        </Text>
                    </Col>
                    <Col span={12}>
                        <div style={{ height: 400, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                            <DeckGL
                                initialViewState={viewState}
                                controller={true}
                                layers={newLayers}
                                onViewStateChange={({ viewState }) => setViewState(viewState)}
                            >
                                <StaticMap
                                    mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
                                    mapStyle={MAPBOX_STYLE}
                                />
                            </DeckGL>
                        </div>
                        <Text type="success" strong style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                            New Route ({newDistance.toFixed(1)} km)
                        </Text>
                    </Col>
                </Row>
            ) : (
                <div 
                    ref={containerRef}
                    style={{ height: 400, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}
                >
                    <DeckGL
                        initialViewState={viewState}
                        controller={true}
                        layers={[...oldLayers, ...newLayers]}
                        onViewStateChange={({ viewState }) => setViewState(viewState)}
                    >
                        <StaticMap
                            mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
                            mapStyle={MAPBOX_STYLE}
                        />
                    </DeckGL>
                </div>
            )}
            <Card size="small" title="Changes Summary" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Statistic
                            title="Distance Change"
                            value={`${distanceDiff > 0 ? '+' : ''}${distanceDiff.toFixed(1)} km`}
                            valueStyle={{ color: distanceDiff > 0 ? '#3f8600' : '#cf1322' }}
                        />
                    </Col>
                    <Col span={12}>
                        <Statistic
                            title="Points Change"
                            value={`${pointsDiff > 0 ? '+' : ''}${pointsDiff}`}
                            valueStyle={{ color: pointsDiff > 0 ? '#3f8600' : '#cf1322' }}
                        />
                    </Col>
                </Row>
            </Card>
        </Card>
    );
};

const calculateShapeDistance = (shape) => {
    let distance = 0;
    for (let i = 1; i < shape.length; i++) {
        const point1 = shape[i - 1];
        const point2 = shape[i];
        distance += calculateDistance(
            { stop_lat: point1.shape_pt_lat, stop_lon: point1.shape_pt_lon },
            { stop_lat: point2.shape_pt_lat, stop_lon: point2.shape_pt_lon }
        );
    }
    return distance;
};

const calculateDistance = (point1, point2) => {
    const R = 6371; // Earth's radius in km
    const lat1 = parseFloat(point1.stop_lat) * Math.PI / 180;
    const lat2 = parseFloat(point2.stop_lat) * Math.PI / 180;
    const dLat = lat2 - lat1;
    const dLon = (parseFloat(point2.stop_lon) - parseFloat(point1.stop_lon)) * Math.PI / 180;

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1) * Math.cos(lat2) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
};

const DiffChecker = () => {
    const [oldGtfs, setOldGtfs] = useState(null);
    const [newGtfs, setNewGtfs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedStop, setSelectedStop] = useState(null);
    const [diffs, setDiffs] = useState(null);

    const handleFileUpload = async (file, type) => {
        setLoading(true);
        try {
            const gtfsProcessor = new GTFSProcessor();
            await gtfsProcessor.loadGTFSFile(file);
            const data = gtfsProcessor.data;
            console.log(`Loaded ${type} GTFS data:`, data);
            if (type === 'old') {
                setOldGtfs(data);
            } else {
                setNewGtfs(data);
            }
        } catch (error) {
            console.error('Error loading GTFS:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (oldGtfs && newGtfs) {
            const newDiffs = calculateDiffs();
            setDiffs(newDiffs);
        }
    }, [oldGtfs, newGtfs]);

    const uploadProps = (type) => ({
        name: 'file',
        multiple: false,
        accept: '.zip',
        showUploadList: false,
        beforeUpload: (file) => {
            handleFileUpload(file, type);
            return false;
        }
    });

    // Calculate differences between the two GTFS feeds
    const calculateDiffs = () => {
        if (!oldGtfs || !newGtfs) {
            console.log('GTFS data not loaded:', { oldGtfs, newGtfs });
            return null;
        }

        console.log('Calculating diffs with:', { 
            oldGtfs: {
                routes: oldGtfs.routes?.length,
                stops: oldGtfs.stops?.length,
                services: oldGtfs.calendar?.length,
                shapes: oldGtfs.shapes?.length,
                trips: oldGtfs.trips?.length
            },
            newGtfs: {
                routes: newGtfs.routes?.length,
                stops: newGtfs.stops?.length,
                services: newGtfs.calendar?.length,
                shapes: newGtfs.shapes?.length,
                trips: newGtfs.trips?.length
            },
        }, oldGtfs, newGtfs);

        // Create empty diff structure if any required data is missing
        const emptyDiff = {
            added: [],
            removed: [],
            modified: []
        };

        const diffs = {
            routes: oldGtfs.routes && newGtfs.routes ? compareRoutes() : emptyDiff,
            stops: oldGtfs.stops && newGtfs.stops ? compareStops() : emptyDiff,
            services: oldGtfs.calendar && newGtfs.calendar ? compareServices() : emptyDiff,
            shapes: oldGtfs.shapes && newGtfs.shapes ? compareShapes() : emptyDiff,
            trips: oldGtfs.trips && newGtfs.trips ? compareTrips() : emptyDiff,
            calendarDates: oldGtfs.calendarDates && newGtfs.calendarDates ? compareCalendarDates() : emptyDiff,
            summary: calculateSummary()
        };

        return diffs;
    };

    const compareRoutes = () => {
        if (!Array.isArray(oldGtfs?.routes) || !Array.isArray(newGtfs?.routes)) {
            console.log('Routes are not arrays:', { 
                oldRoutes: oldGtfs?.routes, 
                newRoutes: newGtfs?.routes 
            });
            return { added: [], removed: [], modified: [] };
        }

        // Create arrays of routes first
        const oldRoutesArray = oldGtfs.routes.filter(r => r && r.route_id);
        const newRoutesArray = newGtfs.routes.filter(r => r && r.route_id);

        // Create maps using objects instead
        const oldRoutes = {};
        const newRoutes = {};

        oldRoutesArray.forEach(r => oldRoutes[r.route_id] = r);
        newRoutesArray.forEach(r => newRoutes[r.route_id] = r);
        
        const changes = {
            added: [],
            removed: [],
            modified: []
        };

        // Find added and modified routes
        newRoutesArray.forEach(route => {
            if (!oldRoutes[route.route_id]) {
                changes.added.push(route);
            } else {
                const oldRoute = oldRoutes[route.route_id];
                if (
                    oldRoute.route_short_name !== route.route_short_name ||
                    oldRoute.route_long_name !== route.route_long_name ||
                    oldRoute.route_type !== route.route_type
                ) {
                    changes.modified.push({
                        old: oldRoute,
                        new: route,
                        changes: getRouteChanges(oldRoute, route)
                    });
                }
            }
        });

        // Find removed routes
        oldRoutesArray.forEach(route => {
            if (!newRoutes[route.route_id]) {
                changes.removed.push(route);
            }
        });

        return changes;
    };

    const compareStops = () => {
        if (!Array.isArray(oldGtfs?.stops) || !Array.isArray(newGtfs?.stops)) {
            return { added: [], removed: [], modified: [] };
        }

        // Create arrays of stops first
        const oldStopsArray = oldGtfs.stops.filter(s => s && s.stop_id);
        const newStopsArray = newGtfs.stops.filter(s => s && s.stop_id);

        // Create maps using objects instead
        const oldStops = {};
        const newStops = {};

        oldStopsArray.forEach(s => oldStops[s.stop_id] = s);
        newStopsArray.forEach(s => newStops[s.stop_id] = s);
        
        const changes = {
            added: [],
            removed: [],
            modified: []
        };

        // Find added and modified stops
        newStopsArray.forEach(stop => {
            if (!oldStops[stop.stop_id]) {
                changes.added.push(stop);
            } else {
                const oldStop = oldStops[stop.stop_id];
                if (
                    oldStop.stop_name !== stop.stop_name ||
                    oldStop.stop_lat !== stop.stop_lat ||
                    oldStop.stop_lon !== stop.stop_lon
                ) {
                    changes.modified.push({
                        old: oldStop,
                        new: stop,
                        changes: getStopChanges(oldStop, stop)
                    });
                }
            }
        });

        // Find removed stops
        oldStopsArray.forEach(stop => {
            if (!newStops[stop.stop_id]) {
                changes.removed.push(stop);
            }
        });

        return changes;
    };

    const compareServices = () => {
        if (!Array.isArray(oldGtfs?.calendar) || !Array.isArray(newGtfs?.calendar)) {
            return { added: [], removed: [], modified: [] };
        }

        const oldServices = {};
        const newServices = {};

        oldGtfs.calendar.forEach(s => oldServices[s.service_id] = s);
        newGtfs.calendar.forEach(s => newServices[s.service_id] = s);
        
        const changes = {
            added: [],
            removed: [],
            modified: []
        };

        // Find added and modified services
        Object.values(newServices).forEach(service => {
            if (!oldServices[service.service_id]) {
                changes.added.push(service);
            } else {
                const oldService = oldServices[service.service_id];
                if (
                    oldService.monday !== service.monday ||
                    oldService.tuesday !== service.tuesday ||
                    oldService.wednesday !== service.wednesday ||
                    oldService.thursday !== service.thursday ||
                    oldService.friday !== service.friday ||
                    oldService.saturday !== service.saturday ||
                    oldService.sunday !== service.sunday ||
                    oldService.start_date !== service.start_date ||
                    oldService.end_date !== service.end_date
                ) {
                    changes.modified.push({
                        old: oldService,
                        new: service,
                        changes: getServiceChanges(oldService, service)
                    });
                }
            }
        });

        // Find removed services
        Object.values(oldServices).forEach(service => {
            if (!newServices[service.service_id]) {
                changes.removed.push(service);
            }
        });

        return changes;
    };

    const compareShapes = () => {
        if (!Array.isArray(oldGtfs?.shapes) || !Array.isArray(newGtfs?.shapes)) {
            return { added: [], removed: [], modified: [] };
        }

        // Group shapes by shape_id
        const oldShapeGroups = {};
        const newShapeGroups = {};

        oldGtfs.shapes.forEach(s => {
            if (!oldShapeGroups[s.shape_id]) oldShapeGroups[s.shape_id] = [];
            oldShapeGroups[s.shape_id].push(s);
        });

        newGtfs.shapes.forEach(s => {
            if (!newShapeGroups[s.shape_id]) newShapeGroups[s.shape_id] = [];
            newShapeGroups[s.shape_id].push(s);
        });

        const changes = {
            added: [],
            removed: [],
            modified: []
        };

        // Find added and modified shapes
        Object.entries(newShapeGroups).forEach(([shapeId, newShapePoints]) => {
            if (!oldShapeGroups[shapeId]) {
                changes.added.push(newShapePoints);
            } else {
                const oldShapePoints = oldShapeGroups[shapeId];
                if (compareShapePoints(oldShapePoints, newShapePoints)) {
                    changes.modified.push({
                        old: oldShapePoints,
                        new: newShapePoints,
                        changes: getShapeChanges(oldShapePoints, newShapePoints)
                    });
                }
            }
        });

        // Find removed shapes
        Object.entries(oldShapeGroups).forEach(([shapeId, oldShapePoints]) => {
            if (!newShapeGroups[shapeId]) {
                changes.removed.push(oldShapePoints);
            }
        });

        return changes;
    };

    const compareShapePoints = (oldShape, newShape) => {
        if (oldShape.length !== newShape.length) return true;
        
        for (let i = 0; i < oldShape.length; i++) {
            if (
                oldShape[i].shape_pt_lat !== newShape[i].shape_pt_lat ||
                oldShape[i].shape_pt_lon !== newShape[i].shape_pt_lon ||
                oldShape[i].shape_pt_sequence !== newShape[i].shape_pt_sequence
            ) {
                return true;
            }
        }
        return false;
    };

    const getShapeChanges = (oldShape, newShape) => {
        return [{
            field: 'Route Path',
            old: `${oldShape.length} points`,
            new: `${newShape.length} points`,
            details: {
                pointsAdded: newShape.length - oldShape.length,
                coordinates: {
                    old: oldShape.map(p => [p.shape_pt_lat, p.shape_pt_lon]),
                    new: newShape.map(p => [p.shape_pt_lat, p.shape_pt_lon])
                }
            }
        }];
    };

    const compareTrips = () => {
        if (!Array.isArray(oldGtfs?.trips) || !Array.isArray(newGtfs?.trips)) {
            return { added: [], removed: [], modified: [] };
        }

        const oldTrips = {};
        const newTrips = {};

        oldGtfs.trips.forEach(t => oldTrips[t.trip_id] = t);
        newGtfs.trips.forEach(t => newTrips[t.trip_id] = t);
        
        const changes = {
            added: [],
            removed: [],
            modified: []
        };

        // Find added and modified trips
        Object.values(newTrips).forEach(trip => {
            if (!oldTrips[trip.trip_id]) {
                changes.added.push(trip);
            } else {
                const oldTrip = oldTrips[trip.trip_id];
                if (
                    oldTrip.route_id !== trip.route_id ||
                    oldTrip.service_id !== trip.service_id ||
                    oldTrip.trip_headsign !== trip.trip_headsign ||
                    oldTrip.direction_id !== trip.direction_id ||
                    oldTrip.shape_id !== trip.shape_id
                ) {
                    changes.modified.push({
                        old: oldTrip,
                        new: trip,
                        changes: getTripChanges(oldTrip, trip)
                    });
                }
            }
        });

        // Find removed trips
        Object.values(oldTrips).forEach(trip => {
            if (!newTrips[trip.trip_id]) {
                changes.removed.push(trip);
            }
        });

        return changes;
    };

    const getTripChanges = (oldTrip, newTrip) => {
        const changes = [];
        
        if (oldTrip.route_id !== newTrip.route_id) {
            const oldRoute = oldGtfs.routes.find(r => r.route_id === oldTrip.route_id);
            const newRoute = newGtfs.routes.find(r => r.route_id === newTrip.route_id);
            changes.push({
                field: 'Route',
                old: oldRoute?.route_short_name || oldTrip.route_id,
                new: newRoute?.route_short_name || newTrip.route_id
            });
        }

        if (oldTrip.trip_headsign !== newTrip.trip_headsign) {
            changes.push({
                field: 'Headsign',
                old: oldTrip.trip_headsign,
                new: newTrip.trip_headsign
            });
        }

        if (oldTrip.direction_id !== newTrip.direction_id) {
            changes.push({
                field: 'Direction',
                old: oldTrip.direction_id,
                new: newTrip.direction_id
            });
        }

        if (oldTrip.shape_id !== newTrip.shape_id) {
            changes.push({
                field: 'Shape',
                old: oldTrip.shape_id,
                new: newTrip.shape_id
            });
        }

        // Compare stop times for this trip
        const oldStopTimes = oldGtfs.stopTimes.filter(st => st.trip_id === oldTrip.trip_id)
            .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));
        const newStopTimes = newGtfs.stopTimes.filter(st => st.trip_id === newTrip.trip_id)
            .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

        if (oldStopTimes.length !== newStopTimes.length) {
            changes.push({
                field: 'Stops',
                old: `${oldStopTimes.length} stops`,
                new: `${newStopTimes.length} stops`,
                details: compareStopSequences(oldStopTimes, newStopTimes)
            });
        } else {
            const timeChanges = compareStopTimes(oldStopTimes, newStopTimes);
            if (timeChanges.length > 0) {
                changes.push({
                    field: 'Times',
                    changes: timeChanges
                });
            }
        }

        return changes;
    };

    const compareStopSequences = (oldStopTimes, newStopTimes) => {
        const changes = {
            added: [],
            removed: [],
            reordered: []
        };

        const oldStops = oldStopTimes.map(st => ({
            stop: oldGtfs.stops.find(s => s.stop_id === st.stop_id),
            sequence: st.stop_sequence
        }));

        const newStops = newStopTimes.map(st => ({
            stop: newGtfs.stops.find(s => s.stop_id === st.stop_id),
            sequence: st.stop_sequence
        }));

        // Find added and removed stops
        const oldStopIds = new Set(oldStops.map(s => s.stop.stop_id));
        const newStopIds = new Set(newStops.map(s => s.stop.stop_id));

        newStops.forEach(stop => {
            if (!oldStopIds.has(stop.stop.stop_id)) {
                changes.added.push(stop);
            }
        });

        oldStops.forEach(stop => {
            if (!newStopIds.has(stop.stop.stop_id)) {
                changes.removed.push(stop);
            }
        });

        // Check for reordering
        const commonStops = oldStops.filter(os => 
            newStops.some(ns => ns.stop.stop_id === os.stop.stop_id)
        );

        commonStops.forEach(oldStop => {
            const newStop = newStops.find(ns => ns.stop.stop_id === oldStop.stop.stop_id);
            if (oldStop.sequence !== newStop.sequence) {
                changes.reordered.push({
                    stop: oldStop.stop,
                    oldSequence: oldStop.sequence,
                    newSequence: newStop.sequence
                });
            }
        });

        return changes;
    };

    const compareStopTimes = (oldStopTimes, newStopTimes) => {
        const changes = [];

        for (let i = 0; i < oldStopTimes.length; i++) {
            const oldTime = oldStopTimes[i];
            const newTime = newStopTimes[i];
            
            if (oldTime.arrival_time !== newTime.arrival_time || oldTime.departure_time !== newTime.departure_time) {
                const stop = oldGtfs.stops.find(s => s.stop_id === oldTime.stop_id);
                changes.push({
                    stop: stop?.stop_name || oldTime.stop_id,
                    sequence: oldTime.stop_sequence,
                    old: {
                        arrival: oldTime.arrival_time,
                        departure: oldTime.departure_time
                    },
                    new: {
                        arrival: newTime.arrival_time,
                        departure: newTime.departure_time
                    }
                });
            }
        }

        return changes;
    };

    const calculateSummary = () => {
        if (!oldGtfs || !newGtfs) return {
            routes: { old: 0, new: 0, diff: 0 },
            stops: { old: 0, new: 0, diff: 0 },
            trips: { old: 0, new: 0, diff: 0 },
            services: { old: 0, new: 0, diff: 0 }
        };

        return {
            routes: {
                old: oldGtfs.routes?.length || 0,
                new: newGtfs.routes?.length || 0,
                diff: (newGtfs.routes?.length || 0) - (oldGtfs.routes?.length || 0)
            },
            stops: {
                old: oldGtfs.stops?.length || 0,
                new: newGtfs.stops?.length || 0,
                diff: (newGtfs.stops?.length || 0) - (oldGtfs.stops?.length || 0)
            },
            trips: {
                old: oldGtfs.trips?.length || 0,
                new: newGtfs.trips?.length || 0,
                diff: (newGtfs.trips?.length || 0) - (oldGtfs.trips?.length || 0)
            },
            services: {
                old: oldGtfs.calendar?.length || 0,
                new: newGtfs.calendar?.length || 0,
                diff: (newGtfs.calendar?.length || 0) - (oldGtfs.calendar?.length || 0)
            }
        };
    };

    const getRouteChanges = (oldRoute, newRoute) => {
        const changes = [];
        if (oldRoute.route_short_name !== newRoute.route_short_name) {
            changes.push({
                field: 'Route Number',
                old: oldRoute.route_short_name,
                new: newRoute.route_short_name
            });
        }
        if (oldRoute.route_long_name !== newRoute.route_long_name) {
            changes.push({
                field: 'Route Name',
                old: oldRoute.route_long_name,
                new: newRoute.route_long_name
            });
        }
        if (oldRoute.route_type !== newRoute.route_type) {
            changes.push({
                field: 'Route Type',
                old: oldRoute.route_type,
                new: newRoute.route_type
            });
        }
        return changes;
    };

    const getStopChanges = (oldStop, newStop) => {
        const changes = [];
        if (oldStop.stop_name !== newStop.stop_name) {
            changes.push({
                field: 'Stop Name',
                old: oldStop.stop_name,
                new: newStop.stop_name
            });
        }
        if (oldStop.stop_lat !== newStop.stop_lat || oldStop.stop_lon !== newStop.stop_lon) {
            changes.push({
                field: 'Location',
                old: `${oldStop.stop_lat}, ${oldStop.stop_lon}`,
                new: `${newStop.stop_lat}, ${newStop.stop_lon}`
            });
        }
        return changes;
    };

    const getServiceChanges = (oldService, newService) => {
        const changes = [];
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        
        const oldDays = days.filter(day => oldService[day] === '1').map(day => day.charAt(0).toUpperCase() + day.slice(1));
        const newDays = days.filter(day => newService[day] === '1').map(day => day.charAt(0).toUpperCase() + day.slice(1));
        
        if (oldDays.join(',') !== newDays.join(',')) {
            changes.push({
                field: 'Operating Days',
                old: oldDays.join(', '),
                new: newDays.join(', ')
            });
        }

        if (oldService.start_date !== newService.start_date || oldService.end_date !== newService.end_date) {
            changes.push({
                field: 'Service Period',
                old: `${dayjs(oldService.start_date, 'YYYYMMDD').format('YYYY-MM-DD')} to ${dayjs(oldService.end_date, 'YYYYMMDD').format('YYYY-MM-DD')}`,
                new: `${dayjs(newService.start_date, 'YYYYMMDD').format('YYYY-MM-DD')} to ${dayjs(newService.end_date, 'YYYYMMDD').format('YYYY-MM-DD')}`
            });
        }

        return changes;
    };

    const compareCalendarDates = () => {
        if (!Array.isArray(oldGtfs?.calendarDates) || !Array.isArray(newGtfs?.calendarDates)) {
            return { added: [], removed: [], modified: [] };
        }
        const key = d => `${d.service_id}_${d.date}`;
        const oldMap = Object.fromEntries(oldGtfs.calendarDates.map(d => [key(d), d]));
        const newMap = Object.fromEntries(newGtfs.calendarDates.map(d => [key(d), d]));
        const changes = { added: [], removed: [], modified: [] };
        Object.values(newMap).forEach(date => {
            if (!oldMap[key(date)]) {
                changes.added.push(date);
            } else if (oldMap[key(date)].exception_type !== date.exception_type) {
                changes.modified.push({
                    old: oldMap[key(date)],
                    new: date
                });
            }
        });
        Object.values(oldMap).forEach(date => {
            if (!newMap[key(date)]) {
                changes.removed.push(date);
            }
        });
        return changes;
    };

    const renderSummary = (diffs) => {
        if (!diffs) return null;

        return (
            <Card>
                <Row gutter={[16, 16]}>
                    <Col span={6}>
                        <Statistic
                            title="Routes"
                            value={diffs.summary.routes.diff}
                            prefix={diffs.summary.routes.diff > 0 ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: diffs.summary.routes.diff > 0 ? '#3f8600' : '#cf1322' }}
                            suffix={`of ${diffs.summary.routes.old}`}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Stops"
                            value={diffs.summary.stops.diff}
                            prefix={diffs.summary.stops.diff > 0 ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: diffs.summary.stops.diff > 0 ? '#3f8600' : '#cf1322' }}
                            suffix={`of ${diffs.summary.stops.old}`}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Trips"
                            value={diffs.summary.trips.diff}
                            prefix={diffs.summary.trips.diff > 0 ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: diffs.summary.trips.diff > 0 ? '#3f8600' : '#cf1322' }}
                            suffix={`of ${diffs.summary.trips.old}`}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Calendar"
                            value={diffs.summary.services.diff}
                            prefix={diffs.summary.services.diff > 0 ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: diffs.summary.services.diff > 0 ? '#3f8600' : '#cf1322' }}
                            suffix={`of ${diffs.summary.services.old}`}
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderRouteChanges = (diffs) => {
        if (!diffs) return null;

        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Route Changes Overview">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title={<Text strong>Only in Old GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.routes.removed}
                                    renderItem={route => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Tag color="blue">{route.route_short_name}</Tag>
                                                <Text delete>{route.route_long_name}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Modified Routes</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.routes.modified}
                                    renderItem={({ old, new: newRoute, changes }) => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Tag color="blue">{old.route_short_name}</Tag>
                                                {changes.map((change, idx) => (
                                                    <div key={idx}>
                                                        <Text strong>{change.field}</Text>
                                                        <div>
                                                            <Text type="danger">- {change.old}</Text>
                                                        </div>
                                                        <div>
                                                            <Text type="success">+ {change.new}</Text>
                                                        </div>
                                                    </div>
                                                ))}
                                                <RouteComparisonMap 
                                                    oldShapes={old}
                                                    newShapes={newRoute}
                                                />
                                                <RouteMetrics
                                                    oldRoute={old}
                                                    newRoute={newRoute}
                                                    oldGtfs={oldGtfs}
                                                    newGtfs={newGtfs}
                                                />
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Only in New GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.routes.added}
                                    renderItem={route => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Tag color="blue">{route.route_short_name}</Tag>
                                                <Text>{route.route_long_name}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Card>
            </Space>
        );
    };

    const RouteMetrics = ({ oldRoute, newRoute, oldGtfs, newGtfs }) => {
        const oldMetrics = calculateRouteMetrics(oldRoute, oldGtfs);
        const newMetrics = calculateRouteMetrics(newRoute, newGtfs);

        return (
            <Card size="small" title="Route Metrics">
                <Row gutter={[16, 8]}>
                    <Col span={8}>
                        <Statistic 
                            title="Total Distance"
                            value={`${(newMetrics.totalDistance - oldMetrics.totalDistance).toFixed(1)} km`}
                            prefix={newMetrics.totalDistance > oldMetrics.totalDistance ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: newMetrics.totalDistance > oldMetrics.totalDistance ? '#cf1322' : '#3f8600' }}
                        />
                    </Col>
                    <Col span={8}>
                        <Statistic 
                            title="Dead Runs"
                            value={`${(newMetrics.deadRuns - oldMetrics.deadRuns).toFixed(1)} km`}
                            prefix={newMetrics.deadRuns > oldMetrics.deadRuns ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: newMetrics.deadRuns > oldMetrics.deadRuns ? '#cf1322' : '#3f8600' }}
                        />
                    </Col>
                    <Col span={8}>
                        <Statistic 
                            title="Stops Served"
                            value={newMetrics.stopsCount - oldMetrics.stopsCount}
                            prefix={newMetrics.stopsCount > oldMetrics.stopsCount ? <PlusOutlined /> : <MinusOutlined />}
                            valueStyle={{ color: newMetrics.stopsCount > oldMetrics.stopsCount ? '#3f8600' : '#cf1322' }}
                        />
                    </Col>
                </Row>
            </Card>
        );
    };

    const calculateRouteMetrics = (route, gtfs) => {
        const trips = gtfs.trips.filter(t => t.route_id === route.route_id);
        const shapes = new Set(trips.map(t => t.shape_id));
        const stopTimes = trips.flatMap(trip => 
            gtfs.stopTimes.filter(st => st.trip_id === trip.trip_id)
        );

        let totalDistance = 0;
        let deadRuns = 0;
        shapes.forEach(shapeId => {
            const shape = gtfs.shapes.filter(s => s.shape_id === shapeId);
            if (shape.length > 0) {
                totalDistance += calculateShapeDistance(shape);
            }
        });

        // Calculate dead runs (distance between last stop of a trip and first stop of next trip)
        trips.forEach(trip => {
            const tripStops = stopTimes
                .filter(st => st.trip_id === trip.trip_id)
                .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));
            
            if (tripStops.length > 0) {
                const lastStop = gtfs.stops.find(s => s.stop_id === tripStops[tripStops.length - 1].stop_id);
                const nextTrip = trips.find(t => t.service_id === trip.service_id && t.trip_id !== trip.trip_id);
                if (nextTrip) {
                    const nextTripFirstStop = gtfs.stops.find(s => 
                        s.stop_id === stopTimes.find(st => 
                            st.trip_id === nextTrip.trip_id && st.stop_sequence === '1'
                        )?.stop_id
                    );
                    if (lastStop && nextTripFirstStop) {
                        deadRuns += calculateDistance(lastStop, nextTripFirstStop);
                    }
                }
            }
        });

        return {
            totalDistance,
            deadRuns,
            stopsCount: new Set(stopTimes.map(st => st.stop_id)).size
        };
    };

    const renderStopChanges = (diffs) => {
        if (!diffs) return null;

        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Stop Changes Overview">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title={<Text strong>Only in Old GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.stops.removed}
                                    renderItem={stop => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text delete>{stop.stop_name}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {stop.stop_lat}, {stop.stop_lon}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Modified Stops</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.stops.modified}
                                    renderItem={({ old, new: newStop, changes }) => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                {changes.map((change, idx) => (
                                                    <div key={idx}>
                                                        <Text strong>{change.field}</Text>
                                                        <div>
                                                            <Text type="danger">- {change.old}</Text>
                                                        </div>
                                                        <div>
                                                            <Text type="success">+ {change.new}</Text>
                                                        </div>
                                                    </div>
                                                ))}
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Only in New GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.stops.added}
                                    renderItem={stop => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text>{stop.stop_name}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {stop.stop_lat}, {stop.stop_lon}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Card>
            </Space>
        );
    };

    const renderServiceChanges = (diffs) => {
        if (!diffs) return null;

        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Calendar (Service Periods) Changes Overview">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title={<Text strong>Only in Old GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.services.removed}
                                    renderItem={service => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text delete>{service.service_id}</Text>
                                                <div>
                                                    {service.monday && <Tag>Mon</Tag>}
                                                    {service.tuesday && <Tag>Tue</Tag>}
                                                    {service.wednesday && <Tag>Wed</Tag>}
                                                    {service.thursday && <Tag>Thu</Tag>}
                                                    {service.friday && <Tag>Fri</Tag>}
                                                    {service.saturday && <Tag>Sat</Tag>}
                                                    {service.sunday && <Tag>Sun</Tag>}
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {dayjs(service.start_date).format('DD/MM/YYYY')} - {dayjs(service.end_date).format('DD/MM/YYYY')}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Modified Services</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.services.modified}
                                    renderItem={({ old, new: newService, changes }) => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text strong>{old.service_id}</Text>
                                                {changes.map((change, idx) => (
                                                    <div key={idx}>
                                                        <Text strong>{change.field}</Text>
                                                        <div>
                                                            <Text type="danger">- {change.old}</Text>
                                                        </div>
                                                        <div>
                                                            <Text type="success">+ {change.new}</Text>
                                                        </div>
                                                    </div>
                                                ))}
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Only in New GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.services.added}
                                    renderItem={service => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text>{service.service_id}</Text>
                                                <div>
                                                    {service.monday && <Tag>Mon</Tag>}
                                                    {service.tuesday && <Tag>Tue</Tag>}
                                                    {service.wednesday && <Tag>Wed</Tag>}
                                                    {service.thursday && <Tag>Thu</Tag>}
                                                    {service.friday && <Tag>Fri</Tag>}
                                                    {service.saturday && <Tag>Sat</Tag>}
                                                    {service.sunday && <Tag>Sun</Tag>}
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {dayjs(service.start_date).format('DD/MM/YYYY')} - {dayjs(service.end_date).format('DD/MM/YYYY')}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Card>
            </Space>
        );
    };

    const renderShapeChanges = (diffs) => {
        if (!diffs) return null;

        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Shape Changes Overview">
                    {/* Removed Shapes */}
                    <Card 
                        type="inner" 
                        title={<Text type="danger">Removed Shapes</Text>}
                        style={{ marginBottom: 16 }}
                    >
                        <List
                            size="small"
                            dataSource={diffs.shapes.removed}
                            renderItem={shape => (
                                <List.Item>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Text delete>{shape[0].shape_id}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            {shape.length} points, {calculateShapeDistance(shape).toFixed(1)} km
                                        </Text>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </Card>

                    {/* Modified Shapes */}
                    <Card 
                        type="inner" 
                        title={<Text type="warning">Modified Shapes</Text>}
                        style={{ marginBottom: 16 }}
                    >
                        <List
                            size="small"
                            dataSource={diffs.shapes.modified}
                            renderItem={({ old, new: newShape, changes }) => (
                                <List.Item>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Text strong>{old[0].shape_id}</Text>
                                        {changes.map((change, idx) => (
                                            <div key={idx}>
                                                <Text strong>{change.field}</Text>
                                                <div>
                                                    <Text type="danger">- {change.old}</Text>
                                                </div>
                                                <div>
                                                    <Text type="success">+ {change.new}</Text>
                                                </div>
                                            </div>
                                        ))}
                                        <RouteComparisonMap 
                                            oldShapes={old}
                                            newShapes={newShape}
                                        />
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </Card>

                    {/* Added Shapes */}
                    <Card 
                        type="inner" 
                        title={<Text type="success">Added Shapes</Text>}
                    >
                        <List
                            size="small"
                            dataSource={diffs.shapes.added}
                            renderItem={shape => (
                                <List.Item>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Text>{shape[0].shape_id}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            {shape.length} points, {calculateShapeDistance(shape).toFixed(1)} km
                                        </Text>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Card>
            </Space>
        );
    };

    const renderTripChanges = (diffs) => {
        if (!diffs) return null;

        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Trip Changes Overview">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title={<Text strong>Only in Old GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.trips.removed}
                                    renderItem={trip => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text delete>{trip.trip_id}</Text>
                                                <div>
                                                    <Tag color="blue">{trip.route_id}</Tag>
                                                    {trip.trip_headsign && <Tag>{trip.trip_headsign}</Tag>}
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    Service: {trip.service_id}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Modified Trips</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.trips.modified}
                                    renderItem={({ old, new: newTrip, changes }) => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text strong>{old.trip_id}</Text>
                                                {changes.map((change, idx) => (
                                                    <div key={idx}>
                                                        <Text strong>{change.field}</Text>
                                                        <div>
                                                            <Text type="danger">- {change.old}</Text>
                                                        </div>
                                                        <div>
                                                            <Text type="success">+ {change.new}</Text>
                                                        </div>
                                                    </div>
                                                ))}
                                                {old.shape_id !== newTrip.shape_id && (
                                                    <RouteComparisonMap 
                                                        oldShapes={oldGtfs.shapes.filter(s => s.shape_id === old.shape_id)}
                                                        newShapes={newGtfs.shapes.filter(s => s.shape_id === newTrip.shape_id)}
                                                    />
                                                )}
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Only in New GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.trips.added}
                                    renderItem={trip => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text>{trip.trip_id}</Text>
                                                <div>
                                                    <Tag color="blue">{trip.route_id}</Tag>
                                                    {trip.trip_headsign && <Tag>{trip.trip_headsign}</Tag>}
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    Service: {trip.service_id}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Card>
            </Space>
        );
    };

    const renderCalendarDatesChanges = (diffs) => {
        if (!diffs) return null;
        return (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Calendar Dates (Service Exceptions) Changes Overview">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title={<Text strong>Only in Old GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.calendarDates.removed}
                                    renderItem={item => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text delete>{item.service_id}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{dayjs(item.date, 'YYYYMMDD').format('YYYY-MM-DD')}</Text>
                                                <Tag color={item.exception_type === '1' ? 'green' : 'red'}>
                                                    {item.exception_type === '1' ? 'Added Service' : 'Removed Service'}
                                                </Tag>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Modified Exceptions</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.calendarDates.modified}
                                    renderItem={({ old, new: newItem }) => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text strong>{old.service_id}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{dayjs(old.date, 'YYYYMMDD').format('YYYY-MM-DD')}</Text>
                                                <div>
                                                    <Text type="danger">- {old.exception_type === '1' ? 'Added Service' : 'Removed Service'}</Text>
                                                </div>
                                                <div>
                                                    <Text type="success">+ {newItem.exception_type === '1' ? 'Added Service' : 'Removed Service'}</Text>
                                                </div>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={<Text strong>Only in New GTFS</Text>} type="inner">
                                <List
                                    size="small"
                                    dataSource={diffs.calendarDates.added}
                                    renderItem={item => (
                                        <List.Item>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text>{item.service_id}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{dayjs(item.date, 'YYYYMMDD').format('YYYY-MM-DD')}</Text>
                                                <Tag color={item.exception_type === '1' ? 'green' : 'red'}>
                                                    {item.exception_type === '1' ? 'Added Service' : 'Removed Service'}
                                                </Tag>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Card>
            </Space>
        );
    };

    const renderFileStats = () => {
        if (!oldGtfs || !newGtfs) return null;

        // Only show standard GTFS files that exist in either zip
        const getFileStats = (gtfs) => {
            const stats = {};
            if (gtfs.agency?.length >= 0) stats.agency = gtfs.agency?.length || 0;
            if (gtfs.stops?.length >= 0) stats.stops = gtfs.stops?.length || 0;
            if (gtfs.routes?.length >= 0) stats.routes = gtfs.routes?.length || 0;
            if (gtfs.trips?.length >= 0) stats.trips = gtfs.trips?.length || 0;
            if (gtfs.stop_times?.length >= 0) stats['stop_times'] = gtfs.stop_times?.length || 0;
            if (gtfs.calendar?.length >= 0) stats.calendar = gtfs.calendar?.length || 0;
            if (gtfs.calendarDates?.length >= 0) stats['calendarDates'] = gtfs.calendarDates?.length || 0;
            if (gtfs.fare_attributes?.length >= 0) stats['fare_attributes'] = gtfs.fare_attributes?.length || 0;
            if (gtfs.shapes?.length >= 0) stats.shapes = gtfs.shapes?.length || 0;
            if (gtfs.frequencies?.length >= 0) stats.frequencies = gtfs.frequencies?.length || 0;
            if (gtfs.transfers?.length >= 0) stats.transfers = gtfs.transfers?.length || 0;
            if (gtfs.pathways?.length >= 0) stats.pathways = gtfs.pathways?.length || 0;
            if (gtfs.levels?.length >= 0) stats.levels = gtfs.levels?.length || 0;
            return stats;
        };

        const oldStats = getFileStats(oldGtfs);
        const newStats = getFileStats(newGtfs);

        // Only show files that exist in either zip
        const allFiles = new Set([...Object.keys(oldStats), ...Object.keys(newStats)]);

        const calculateDiff = (oldVal, newVal) => {
            const diff = newVal - oldVal;
            return {
                diff,
                percentage: oldVal === 0 ? 0 : ((diff / oldVal) * 100).toFixed(1)
            };
        };

        const statsData = Array.from(allFiles).map(key => {
            const { diff, percentage } = calculateDiff(oldStats[key] || 0, newStats[key] || 0);
            return {
                name: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                oldCount: oldStats[key] || 0,
                newCount: newStats[key] || 0,
                difference: diff,
                percentageChange: percentage
            };
        });

        return (
            <Card title="File Statistics" style={{ marginBottom: 16 }}>
                <Table
                    dataSource={statsData}
                    pagination={false}
                    size="small"
                    columns={[
                        {
                            title: 'File Type',
                            dataIndex: 'name',
                            key: 'name',
                            render: text => <Text strong>{text === 'CalendarDates' ? 'Calendar Dates' : text}</Text>
                        },
                        {
                            title: 'Old Count',
                            dataIndex: 'oldCount',
                            key: 'oldCount',
                            align: 'right'
                        },
                        {
                            title: 'New Count',
                            dataIndex: 'newCount',
                            key: 'newCount',
                            align: 'right'
                        },
                        {
                            title: 'Difference',
                            dataIndex: 'difference',
                            key: 'difference',
                            align: 'right',
                            render: (diff) => (
                                <Text type={diff > 0 ? 'success' : diff < 0 ? 'danger' : undefined}>
                                    {diff > 0 ? '+' : ''}{diff}
                                </Text>
                            )
                        },
                        {
                            title: 'Change %',
                            dataIndex: 'percentageChange',
                            key: 'percentageChange',
                            align: 'right',
                            render: (percent) => (
                                <Text type={percent > 0 ? 'success' : percent < 0 ? 'danger' : undefined}>
                                    {percent > 0 ? '+' : ''}{percent}%
                                </Text>
                            )
                        }
                    ]}
                />
            </Card>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card>
                    <Title level={3}>GTFS Diff Checker</Title>
                    <Text>
                        Compare two GTFS feeds to identify changes in routes, stops, and services.
                    </Text>
                </Card>

                <Row gutter={16}>
                    <Col span={12}>
                        <Dragger {...uploadProps('old')}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Upload Old GTFS</p>
                            <p className="ant-upload-hint">
                                {oldGtfs ? 'GTFS loaded successfully' : 'Click or drag file to this area to upload'}
                            </p>
                        </Dragger>
                    </Col>
                    <Col span={12}>
                        <Dragger {...uploadProps('new')}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Upload New GTFS</p>
                            <p className="ant-upload-hint">
                                {newGtfs ? 'GTFS loaded successfully' : 'Click or drag file to this area to upload'}
                            </p>
                        </Dragger>
                    </Col>
                </Row>

                {renderFileStats()}

                {diffs && (
                    <>
                        {renderSummary(diffs)}
                        <Card>
                            <Tabs
                                items={[
                                    {
                                        key: 'routes',
                                        label: (
                                            <span>
                                                Routes
                                                <Tag style={{ marginLeft: 8 }}>
                                                    {diffs.routes.added.length + diffs.routes.modified.length + diffs.routes.removed.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: renderRouteChanges(diffs)
                                    },
                                    {
                                        key: 'stops',
                                        label: (
                                            <span>
                                                Stops
                                                <Tag style={{ marginLeft: 8 }}>
                                                    {diffs.stops.added.length + diffs.stops.modified.length + diffs.stops.removed.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: renderStopChanges(diffs)
                                    },
                                    {
                                        key: 'services',
                                        label: (
                                            <span>
                                                Calendar
                                                <Tag style={{ marginLeft: 8 }}>
                                                    {diffs.services.added.length + diffs.services.modified.length + diffs.services.removed.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: renderServiceChanges(diffs)
                                    },
                                    {
                                        key: 'shapes',
                                        label: (
                                            <span>
                                                Shapes
                                                <Tag style={{ marginLeft: 8 }}>
                                                    {diffs.shapes.added.length + diffs.shapes.modified.length + diffs.shapes.removed.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: renderShapeChanges(diffs)
                                    },
                                    {
                                        key: 'trips',
                                        label: (
                                            <span>
                                                Trips
                                                <Tag style={{ marginLeft: 8 }}>
                                                    {diffs.trips.added.length + diffs.trips.modified.length + diffs.trips.removed.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: renderTripChanges(diffs)
                                    },
                                    {
                                        key: 'calendarDates',
                                        label: (
                                            <span>
                                                Calendar Dates
                                                <Tag style={{ marginLeft: 8 }}>
                                                    {diffs.calendarDates.added.length + diffs.calendarDates.modified.length + diffs.calendarDates.removed.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: renderCalendarDatesChanges(diffs)
                                    }
                                ]}
                            />
                        </Card>
                    </>
                )}
            </Space>
        </div>
    );
};

export default DiffChecker;